import * as React from 'react';
import {
  MOCK_PAY_RUNS,
  MOCK_SESSION,
  MOCK_ORGANISATION_SETTINGS,
  MOCK_UPDATE_REQUESTS,
} from './mock_data';
import type {
  AppRole,
  PayRun,
  OrganisationSettings,
  SessionUser,
  UpdateRequest,
} from './types';
import {
  deriveSessionFromIdentity,
  type GrantClaim,
  type IdentityClaim,
} from './wagepro_store_identity';
import {
  deriveSessionFromFacts,
  type SessionFacts,
} from './wagepro_store_session_facts';
import { resolveEmployeeViewOnly } from './employee_view_only';
import { isNative } from '../lib/platform';
import {
  deriveOrganisationSettings,
  planOrganisationSave,
  type OrganisationUpdateFields,
  type RealOrganisationRecord,
} from './wagepro_store_settings';

/**
 * In-memory store standing in for the payroll API.
 *
 * It exists so that a mutation on one screen is visible on another, exactly
 * as it will be once these reads are tRPC queries and these writes are
 * mutations. Screens never touch this module directly — they go through the
 * `use_*` hooks, which are the single seam the backend pass will
 * re-implement.
 *
 * The employee directory no longer lives here (C2, #22326 — see
 * `data/use_employees.ts`): it moved to a fully self-fetching real hook,
 * the same "no store seam" shape `use_my_pay.ts`/`use_session_facts.ts`
 * already established for My Pay and session facts.
 *
 * `expenses`/`approve_expense`/`reject_expense` (and their `MOCK_EXPENSES`
 * seed) are REMOVED entirely (#22792) — the Expenses screen is now wired to
 * the real `expenseClaim.list`/`.approve`/`.reject` backend via
 * `data/use_expenses.ts`, the same fully self-fetching "no store seam" shape
 * every other real C-child hook already established. `use_dashboard.ts`'s
 * `pendingExpenses`/nav-bell figure, which used to read this store's
 * `expenses`, is documented at that hook's own doc comment — it does not
 * pick up this real data (kept deliberately out of scope for this pass; see
 * that file).
 *
 * The dashboard's composite KPI summary (`dashboard_summary`/
 * `joiners_missing_bank_count`/`activity_now`) ALSO no longer lives here
 * (C4, #22328 — see `data/use_dashboard.ts`): it moved to its own
 * self-fetching hook for the exact same reason the employee directory and
 * pay-run history did — `filing_calendar`/`activity_audit_rows` are
 * consequently no longer provider props either.
 *
 * `pay_runs` (C12, #22334) is seeded from `MOCK_PAY_RUNS` and mutated only by
 * `acknowledge_exception` — no consumer reads it: `use_pay_runs.ts` sources
 * real runs itself and destructures only `acknowledge_exception`/
 * `pay_run_post_actions` from this store. Acking an exception on a real run
 * therefore looks up a real id in this mock list and no-ops — `acknowledge_exception`
 * itself is UNCHANGED (C12 is a removal pass, not where the real fix belongs;
 * #22450 tracks wiring it to a real backend mutation). What IS new (C12
 * follow-up, independent adversarial review of PR #22453 finding F4): the
 * no-op is no longer silent from the user's side. `use_pay_runs.ts`'s
 * `exception_ack_available` compares the CURRENT real pay run's id against
 * this mock list and reports `false` for any real run (which never matches);
 * `run_payroll_screen.tsx`/`ExceptionsStep` use that to hide the
 * "acknowledge" button entirely rather than let a payroll admin click it and
 * believe an exception was handled when nothing happened. The `pay_run_records` prop and
 * `has_posted_pay_run` field that used to seed a real-data-derived
 * currency-lock flag off this list are gone entirely (C12) — nothing wired
 * them (nothing ever passed `pay_run_records` to this provider), so removing
 * them is dead-code deletion, not a behavior change. See
 * `use_settings.ts`'s `resolveHasPostedPayRun` for the real,
 * `use_pay_runs.ts`-sourced replacement.
 *
 * Data Centre allowances (#22728) ALSO no longer live here — they moved to
 * `data/use_data_centre.ts`, a fully self-fetching real hook over
 * `payroll.listPayItems`/`createPayItem`/`updatePayItem`, the same
 * "no store seam" shape the employee directory and dashboard summary
 * already established. `MOCK_ALLOWANCES` is deleted from `mock_data.ts`
 * (this store was its last production reader).
 *
 * `policies`/`toggle_policy` (C12, #22334's `MOCK_POLICIES`) are REMOVED
 * entirely as of #22730, not migrated to a real backend. The Settings >
 * Policies tab (`overtime`/`expense`/`leave_carry_forward` toggles) had no
 * production reader to begin with (`settings_screen.tsx` never rendered
 * this field on any tab), and none of the three toggles maps onto a real
 * concept in the new `entity_payroll_settings` model (#22729): the
 * `overtime` toggle's `multiple_bp`/`weekly_threshold_hours` params are a
 * different concept from that model's `overtime_hours_divisor` (an
 * hourly-rate divisor, not an overtime multiplier/threshold), and
 * `expense`/`leave_carry_forward` are general-HR concepts with no payroll
 * config equivalent at all. See #22451 (the permanently-mock HR-surfaces
 * umbrella tracker) for the disposition note.
 */
/**
 * The outcome of a real Organisation-settings save (C6, #21937; wired to the
 * REAL production bearer client instead of the dev-bypass cookie client by
 * C11, #22333) -- deliberately its own type rather than importing
 * `use_settings.ts`'s `saveRealOrganisationSettings` return type directly, so
 * this store module stays agnostic to how the controller is actually
 * implemented (real production, a future test double, ...) -- `main.tsx`
 * adapts. `record`/`reason` are present only on their respective
 * `ok: true`/`ok: false` branches, mirrored loosely here as both-optional.
 * `reason: 'entitlement_required'` is carried for classifier symmetry with
 * every other C-child's add-on-gated save/write path (C2/C3/C7/C8/C9) -- not
 * reachable via `entity.update` today (see `lib/trpc.ts`'s
 * `isPayrollAddonRequiredError` docblock), so `use_settings.ts`'s
 * `update_organisation` wrapper collapses it into the same generic
 * non-`currency_locked` failure the UI already handles.
 */
export interface OrganisationSaveOutcome {
  ok: boolean;
  record?: RealOrganisationRecord;
  reason?: 'currency_locked' | 'entitlement_required' | 'error';
}

/** Real backend action for saving Organisation settings, wired to the real production bearer client (C11, #22333). See `WageProStoreProvider`'s `organisation_actions` prop doc. */
export interface OrganisationActionsController {
  save: (input: OrganisationUpdateFields) => Promise<OrganisationSaveOutcome>;
}

/** The four SL statutory remittance types — mirrors `RealStatutoryPaymentType` (`lib/auth/oauth_statutory_filing.ts`), redefined here rather than imported so this module never imports from `lib/auth` directly; `main.tsx`/`use_pay_run_post_actions.ts` adapt. */
export type StatutoryPaymentType =
  | 'epf_employee'
  | 'epf_employer'
  | 'etf'
  | 'paye';

/** One recorded statutory remittance, as surfaced to `PostRunStep`. */
export interface StatutoryPaymentRecord {
  id: string;
  payment_type: StatutoryPaymentType;
  amount_cents: number;
  paid_on: string;
  reference: string | null;
}

/** A bank/cash account eligible as a statutory-payment source, for the record-payment dialog's picker. */
export interface BankAccountOption {
  id: string;
  code: string | null;
  name: string;
}

/**
 * A bank/cash account eligible as a salary-disbursement debit source (C8,
 * #22330), sourced from C5's narrow `payroll.listPaymentSourceAccounts`
 * (#22324) — deliberately a SEPARATE type from {@link BankAccountOption}
 * even though the two shapes overlap and are now (C9, #22331) both real,
 * OAuth-backed, and sourced from this SAME procedure: that one (the
 * statutory-payment picker) is mapped down from this shape, dropping
 * `currency_code`, which the statutory dialog never shows; this one backs
 * the disbursement-file picker and keeps `currency_code`, the entity's
 * `bank_accounts` row's currency when one is linked, `null` otherwise
 * (mirrors `RealPaymentSourceAccountRow`, `oauth_pay_run_post_actions.ts`).
 */
export interface PaymentSourceAccountOption {
  id: string;
  name: string;
  code: string | null;
  currency_code: string | null;
}

/** How a real, OAuth-backed post-run action can fail, once classified — see `oauth_pay_run_post_actions.ts`'s `isPayrollAddonRequiredError` docblock for why this is a NARROW positive classification, not a bare FORBIDDEN check. */
export type PayrollActionFailureReason = 'entitlement_required' | 'error';

/**
 * How a real, OAuth-backed filing-calendar WRITE call can fail, once
 * classified — mirrors `oauth_statutory_filing.ts`'s identically-named type
 * (redefined here rather than imported, same "never import from lib/auth
 * directly" stance as {@link PayrollActionFailureReason}); see that module's
 * doc comment for the full rationale behind each member.
 */
export type FilingCalendarFailureReason =
  | 'permission_required'
  | 'jurisdiction_unsupported'
  | 'error';

/** A raw filing-obligation row, for the filing-calendar panel. */
export interface FilingObligationSummary {
  obligation_id: string;
  report_id: string;
  due_date: string;
  status: string;
}

/**
 * Real backend actions for `PostRunStep` (C4c #21956; payslip/disbursement
 * /bank-picker migrated to a REAL OAuth-backed executor by C8 #22330;
 * statutory payments + the filing calendar migrated by C9 #22331 —
 * `dev_pay_run_post_actions.ts` is DELETED, this controller is fully real in
 * every environment now) — payslip download, salary disbursement file,
 * statutory payment recording/voiding, and the entity-wide filing calendar.
 * Deliberately looser than `oauth_pay_run_post_actions.ts`/
 * `oauth_statutory_filing.ts`'s own discriminated result types (same "never
 * import from lib/auth directly" stance as `OrganisationSaveOutcome`
 * above); `main.tsx`/`use_pay_run_post_actions.ts` adapt. Every method
 * degrades to `{ ok: false }` (or an explicit failure reason) on failure
 * rather than throwing or fabricating data — see each resolver's own
 * docblock for the specific entitlement/tier/feature gates that can cause
 * that.
 *
 * ── Read-only-grant write gating (C9, #22331) ─────────────────────────────
 *
 * `record_statutory_payment`, `void_statutory_payment`,
 * `generate_upcoming_filings`, `mark_filed`, and `mark_submitted` (#23316) are
 * the five WRITE actions —
 * each is `| undefined` (hiding the affordance entirely, not merely
 * disabling it) when `is_read_only_grant` is `true`. See
 * `use_pay_runs.ts`'s `isWriteCapableGrant` (reused here, not
 * reimplemented — see `use_pay_run_post_actions.ts`'s module docblock) for
 * the exact rule and `post_run_sections.tsx` for how
 * `StatutorySection`/`FilingSection` consume `is_read_only_grant` to show an
 * explanatory notice.
 */
export interface PayRunPostActionsController {
  /**
   * Real, OAuth-backed (C8, #22330) — see `oauth_pay_run_post_actions.ts`'s
   * `fetchGeneratedPayslip`. The `ok: false` branch mirrors
   * `GeneratePayslipResult` exactly (a discriminated union carrying
   * `message` only on the generic `'error'` reason, never on
   * `'entitlement_required'`) rather than collapsing to the looser
   * `PayrollActionFailureReason` — a prior version of this type dropped
   * `message` entirely, so the real server detail `fetchGeneratedPayslip`
   * already captures could never reach the UI's own console.error, even for
   * debugging (adversarial-review finding, C8 second round, #22410).
   */
  generate_payslip: (input: {
    pay_run_id: string;
    employee_id: string;
    locale: 'en' | 'si' | 'ta';
  }) => Promise<
    | { ok: true; pdf_base64: string; filename: string }
    | { ok: false; reason: 'entitlement_required' }
    | { ok: false; reason: 'error'; message: string }
  >;
  /** Real, OAuth-backed (C8, #22330) — see `oauth_pay_run_post_actions.ts`'s `fetchSalaryDisbursementFile`. */
  get_salary_disbursement_file: (input: {
    pay_run_id: string;
    bank_id: 'sampath' | 'combank' | 'hnb' | 'boc';
    debit_account_no: string;
    value_date: string;
  }) => Promise<
    | {
        ok: true;
        filename: string;
        mime_type: string;
        content: string;
        total_cents: number;
        line_count: number;
        bank_name: string;
        /**
         * Named per-employee exclusion warnings from the server (missing
         * bank details, non-positive net pay, …) — MUST be surfaced to the
         * user alongside the success state. A downloaded file that silently
         * dropped employees is a real payroll-correctness gap, not a nice-to
         * -have (adversarial review finding #1, C4c #21956): the production
         * `apps/ledger-web` equivalent (`salary_disbursement_dialog.tsx`)
         * renders these; this dev-bypass surface must not regress that.
         */
        warnings: string[];
      }
    | { ok: false; reason: 'entitlement_required' }
    | { ok: false; reason: 'error'; message: string }
  >;
  /**
   * Real, OAuth-backed (C8, #22330), C5's narrow
   * `payroll.listPaymentSourceAccounts` (#22324) — the disbursement
   * dialog's OWN bank-account picker.
   */
  list_payment_source_accounts: () => Promise<
    | { ok: true; accounts: PaymentSourceAccountOption[] }
    | { ok: false; reason: PayrollActionFailureReason }
  >;
  /**
   * Real, OAuth-backed (C9, #22331) — the statutory-payment dialog's OWN
   * bank-account picker. Reuses the SAME `payroll.listPaymentSourceAccounts`
   * procedure as {@link PayRunPostActionsController.list_payment_source_accounts}
   * above (never `account.list`, which is deliberately not OAuth-allowlisted
   * for a payroll token), mapped down to the narrower `BankAccountOption`
   * shape — see `oauth_statutory_filing.ts`'s module docblock for why a
   * second, separate network call was not introduced.
   */
  list_bank_accounts: () => Promise<
    { ok: true; accounts: BankAccountOption[] } | { ok: false }
  >;
  /** Real, OAuth-backed (C9, #22331) — see `oauth_statutory_filing.ts`'s `fetchStatutoryPayments`. `computed_due_cents` is read verbatim from the server, never recomputed client-side. */
  list_statutory_payments: (pay_run_id: string) => Promise<
    | {
        ok: true;
        computed_due_cents: Record<StatutoryPaymentType, number>;
        payments: StatutoryPaymentRecord[];
      }
    | { ok: false; reason: PayrollActionFailureReason }
  >;
  /**
   * Real, OAuth-backed (C9, #22331). WRITE action — `undefined` for a
   * read-only grant (`is_read_only_grant: true`), hiding the affordance
   * entirely. Never optimistic: the caller only learns of success once the
   * server confirms it. `reason: 'entitlement_required'` is a SEPARATE
   * branch from `'error'` (never a bare `{ reason: string }`) so the UI can
   * never accidentally render the internal `'entitlement_required'` token
   * itself as alert copy (adversarial-review finding).
   *
   * `reason: 'step_up_required'` (#22829) is the AAL2 step-up rejection from
   * `assertMoneyWriteAllowed` — mirrors `use_pay_runs.ts`'s
   * `PayRunMutationFailure` (#22821) identical addition for
   * `approvePayRun`/`startPayRun`. A blind retry can never clear this (an
   * AAL1 token never self-upgrades; only a fresh sign-in round trip does),
   * so `post_run_sections.tsx` renders dedicated 2FA guidance for this
   * branch rather than the generic error copy.
   */
  record_statutory_payment?: (input: {
    pay_run_id: string;
    payment_type: StatutoryPaymentType;
    amount_cents: number;
    paid_on: string;
    reference?: string;
    bank_account_id: string;
  }) => Promise<
    | { ok: true }
    | { ok: false; reason: 'entitlement_required' }
    | { ok: false; reason: 'step_up_required' }
    | { ok: false; reason: 'error'; message: string }
  >;
  /**
   * Real, OAuth-backed (C9, #22331). WRITE action — `undefined` for a
   * read-only grant. Never optimistic. Same discriminated failure shape as
   * {@link PayRunPostActionsController.record_statutory_payment}, including
   * `reason: 'step_up_required'` (#22829).
   */
  void_statutory_payment?: (input: {
    pay_run_id: string;
    payment_id: string;
  }) => Promise<
    | { ok: true }
    | { ok: false; reason: 'entitlement_required' }
    | { ok: false; reason: 'step_up_required' }
    | { ok: false; reason: 'error'; message: string }
  >;
  /**
   * Real, OAuth-backed (C9, #22331). WRITE action — `undefined` for a
   * read-only grant. `reason` is discriminated (never the raw server/network
   * message — see {@link FilingCalendarFailureReason}'s doc comment) so the
   * UI always shows translated copy, never an untranslated English sentence
   * from a FORBIDDEN/NOT_FOUND error (adversarial-review finding: this was
   * previously a bare `string`, which surfaced raw tRPC error text verbatim).
   */
  generate_upcoming_filings?: () => Promise<
    | { ok: true; inserted: number; skipped: number }
    | { ok: false; reason: FilingCalendarFailureReason }
  >;
  /**
   * Real, OAuth-backed (C9, #22331) — see `oauth_statutory_filing.ts`'s
   * `listFilingObligations`. `reason` is the SAME discriminated
   * `FilingCalendarFailureReason` the WRITE actions below use — never a
   * bare `{ ok: false }` (adversarial-review finding, C9 second round,
   * #22415: an earlier version discarded `listFilingObligations`'s own
   * classified `reason` here, collapsing every load failure — including
   * `permission_required`, concretely reachable today since
   * `filingCalendar.list` isn't OAuth-allowlisted until C6/#22325 fully
   * lands — to one generic "could not load" message instead of the more
   * specific, already-translated copy `FILING_FAILURE_KEY`-style mappings
   * in `post_run_sections.tsx` already have for this exact reason).
   */
  list_filing_obligations: () => Promise<
    | { ok: true; obligations: FilingObligationSummary[] }
    | { ok: false; reason: FilingCalendarFailureReason }
  >;
  /**
   * Real, OAuth-backed (C9, #22331). WRITE action — `undefined` for a
   * read-only grant. Same discriminated failure shape as
   * {@link PayRunPostActionsController.generate_upcoming_filings}.
   */
  mark_filed?: (input: {
    obligation: string;
    period_label: string;
  }) => Promise<
    { ok: true } | { ok: false; reason: FilingCalendarFailureReason }
  >;
  /**
   * Real, OAuth-backed (#23316). WRITE action — `undefined` for a read-only
   * grant. Legacy sibling of {@link PayRunPostActionsController.mark_filed},
   * keyed by opaque `obligation_id` ULID rather than
   * `obligation:period_label`. Same discriminated failure shape — the
   * NOT_FOUND ambiguity between the LK-jurisdiction gate and the "obligation
   * missing" branch is documented on `markSubmitted` in
   * `oauth_statutory_filing.ts`.
   */
  mark_submitted?: (input: {
    obligation_id: string;
  }) => Promise<
    { ok: true } | { ok: false; reason: FilingCalendarFailureReason }
  >;
  /**
   * `true` when the resolved OAuth grant is NOT write-capable
   * (`use_pay_runs.ts`'s `isWriteCapableGrant` returned `false` —
   * `use_pay_run_post_actions.ts` imports it from there rather than
   * reimplementing it; see that module's docblock) — the five write
   * methods above are `undefined` exactly when
   * this is `true`. `post_run_sections.tsx` reads this to show an
   * explanatory read-only notice rather than letting the buttons just
   * silently vanish with no explanation.
   */
  is_read_only_grant: boolean;
}

interface WageProStoreValue {
  session: SessionUser;
  set_role: (role: AppRole) => void;
  /**
   * `true` for a REAL employee (self-service) session. Consumers use it to
   * hide employer-only affordances — notably `EmployeeTopBar`'s "Switch to
   * admin view", which exists for an EMPLOYER previewing the employee surface
   * and must not be offered to an actual employee.
   */
  is_employee_session: boolean;
  /**
   * `true` when this runtime/session can NEVER reach the employer (admin)
   * surface — a real employee session, OR the native Capacitor shell, where
   * the employer console is deliberately not offered at all. See
   * `./employee_view_only.ts` for both reasons. This is the flag consumers
   * should read (via `use_session.ts`'s `can_switch_to_admin`);
   * `is_employee_session` above answers the narrower "is this person an
   * employee", which is NOT the same question once the shell is in play.
   */
  is_employee_view_only: boolean;

  update_requests: UpdateRequest[];
  approve_update_request: (id: string) => void;
  reject_update_request: (id: string) => void;

  pay_runs: PayRun[];
  acknowledge_exception: (run_id: string, exception_id: string) => void;
  /**
   * Real post-run actions for the run-payroll wizard's final step (C4c,
   * #21956; fully real/OAuth-backed as of C9, #22331) — payslip download,
   * salary disbursement file, statutory payment recording/voiding, and the
   * filing calendar. `undefined` only when the OAuth entity context itself
   * isn't `'ready'` (see the provider prop below) — `PostRunStep` hides
   * these actions entirely in that case rather than offering buttons that
   * can't do anything.
   */
  pay_run_post_actions: PayRunPostActionsController | undefined;

  organisation: OrganisationSettings;
  /**
   * Saves Organisation settings. Always resolves an `OrganisationSaveOutcome`
   * — never throws — so `use_settings.ts` can uniformly `await` either the
   * real or the mock path (C6, #21937).
   *
   * Real backend mode (`organisation_actions` present): only the fields that
   * actually changed from the last-loaded/last-persisted real record are
   * sent (`buildEntityUpdateInput`, via `planOrganisationSave` — see
   * `wagepro_store_settings.ts`'s docblocks for why an unchanged
   * `currency_code` is diffed out too, even though it wouldn't itself trip
   * the backend's lock). On success, `organisation` (and the internal
   * last-persisted record used for the NEXT diff) are updated from the
   * mutation's own response. On failure (including a `currency_locked`
   * rejection), `organisation` is left COMPLETELY untouched — the caller
   * (`SettingsScreen`) is responsible for resetting only the locked field in
   * its own local draft state; this store never fabricates a partial
   * success.
   *
   * Mock mode (`organisation_actions` absent): unchanged behavior from
   * before this pass — `org` is accepted verbatim and always "succeeds".
   */
  update_organisation: (
    org: OrganisationSettings,
  ) => Promise<OrganisationSaveOutcome>;
}

const WageProStoreContext = React.createContext<WageProStoreValue | undefined>(
  undefined,
);

export function WageProStoreProvider({
  children,
  identity,
  grant,
  session_facts,
  pay_run_post_actions,
  organisation_record,
  organisation_actions,
  is_employee_session,
  is_native = isNative(),
}: {
  children: React.ReactNode;
  /**
   * The "Sign in with Ledger" identity claim, when a real session exists.
   * Absent (the default) preserves today's fully-mocked session — passing
   * it only re-sources `name`/`initials`; `role`/`tint`/`company_name` stay
   * mock-sourced (see `wagepro_store_identity.ts`).
   */
  identity?: IdentityClaim;
  /**
   * The token's scoped firm/entity OAuth grant (#21929 PR4), when a real
   * session exists. Absent (the default) leaves `session.firm_id`/
   * `entity_id` unset — only surfaced here for a future backend-wiring pass
   * to consume; nothing in this mock store reads them yet.
   */
  grant?: GrantClaim;
  /**
   * Real `payroll_role`/`is_adviser`/`company_name` facts, resolved via the
   * real bearer client in EVERY environment including production (C1,
   * #22323, `../data/use_session_facts.ts`) — unlike most of this
   * provider's other seed props, this is no longer a dev-bypass-only
   * convenience. `main.tsx`'s `AuthGate` only mounts this provider once
   * `useSessionFacts()` resolves `ready`, so `session_facts` is effectively
   * REQUIRED on the real path by the time this component renders; it stays
   * optional here (rather than a required prop) only so this provider keeps
   * working unmodified for any test harness that mounts it directly without
   * going through `AuthGate`. Absent (the default, e.g. in such a test)
   * leaves `role`/`tint`/`company_name` exactly as `deriveSessionFromIdentity`
   * left them (see `wagepro_store_session_facts.ts`) — only seeds the
   * INITIAL session value; `set_role`/`toggle_role` (the "Preview as
   * employee" toggle) continue to layer client-side state on top exactly as
   * before.
   */
  session_facts?: SessionFacts;
  /**
   * `true` when this session is a REAL employee (self-service) session rather
   * than an employer previewing the employee view.
   *
   * Gates `set_role`: an employer toggling "Preview as employee" must be able
   * to switch back, but an employee must never be able to reach the admin
   * surface at all. Threaded from `main.tsx`'s `sessionKind`, which comes from
   * the boot itself (`resolveAuthBoot`) — NOT derived from `session.role`,
   * which is exactly the value the toggle mutates and so cannot distinguish
   * "is an employee" from "is an employer currently viewing as one".
   */
  is_employee_session?: boolean;
  /**
   * Whether this is the native Capacitor shell (`apps/wagepro-mobile`) rather
   * than a browser. Defaults to `lib/platform.ts`'s `isNative()` — the real
   * runtime answer — and is a prop at all only so a test can exercise the
   * shell's behaviour without a device (the same dependency-injection shape
   * `lib/auth/native_session_store.ts` uses for its own `isNative` seam).
   *
   * Together with `is_employee_session` it feeds `resolveEmployeeViewOnly`:
   * inside the shell the employer surface is not offered, so the session
   * boots into the employee view and `set_role('admin')` is refused, exactly
   * as for a real employee session.
   */
  is_native?: boolean;
  /**
   * Real post-run action executor (C4c #21956; fully real/OAuth-backed as of
   * C9 #22331 — `use_pay_run_post_actions.ts`'s `usePayRunPostActions()`),
   * present whenever the OAuth entity context is `'ready'` — in practice
   * always, by the time `AuthGate` mounts this provider (see that hook's
   * docblock). Absent only in that theoretical not-yet-ready window, in
   * which case `pay_run_post_actions` is `undefined` and `PostRunStep` hides
   * every action. None of these actions mutate `pay_runs` itself
   * (payslip/disbursement are downloads; statutory payments and filings are
   * their own, separately-fetched read models `PostRunStep` owns locally) —
   * see that component for why no additional store state is needed here.
   * The run-payroll wizard's own write path (start run + sign-off) no
   * longer runs through this store at all as of C7 (#22329) — see
   * `data/use_pay_runs.ts`. The run-payroll history screen's audit trail
   * (`pay_run_audit_by_run_id`, formerly seeded here) no longer runs
   * through this store either as of C10 (#22332) — see
   * `use_pay_run_audit.ts`'s real, on-demand, per-run hook.
   */
  pay_run_post_actions?: PayRunPostActionsController;
  /**
   * Real Organisation settings record, when resolved (C6, #21937, see
   * `wagepro_store_settings.ts`). As of C11 (#22333), sourced from
   * `useSessionFacts()`'s ALREADY-fetched `entity.clientGet` response
   * (`main.tsx` passes `sessionFactsState.organisation` here directly — see
   * `use_session_facts.ts`'s module docblock on why this is the one shared
   * round trip, never a second independent `entity.clientGet` call). Absent
   * (the default) leaves `organisation` exactly as the mock settings — only
   * seeds the INITIAL value. Also seeds the internal
   * "last-loaded/last-persisted" record `update_organisation` diffs future
   * saves against.
   */
  organisation_record?: RealOrganisationRecord;
  /**
   * Real mutation executor for saving Organisation settings — wired to the
   * REAL production bearer client as of C11 (#22333;
   * `use_settings.ts`'s `saveRealOrganisationSettings`, replacing the
   * dev-bypass-only `lib/auth/dev_settings.ts`, deleted this pass). `main.tsx`
   * builds this via `resolveOrganisationActions` (`use_settings.ts`), which
   * only wires a real controller once a real entity context is bound to the
   * OAuth grant AND the real organisation READ has genuinely succeeded — the
   * `hasRealOrganisation` guard, mirroring `pay_run_actions`'s
   * `hasRealPayRuns` gate: a real controller over an absent/mock record must
   * never activate, since it would route mock state into the real API.
   * Absent (the default) leaves `update_organisation` as today's plain
   * synchronous-resolving mock setter — no regression to existing mock-only
   * behavior when no real controller is wired (e.g. a test harness mounting
   * this provider directly).
   */
  organisation_actions?: OrganisationActionsController;
}): React.JSX.Element {
  const employee_view_only = resolveEmployeeViewOnly({
    is_employee_session: is_employee_session === true,
    is_native,
  });
  const [session, set_session] = React.useState<SessionUser>(() => {
    const derived = deriveSessionFromFacts(
      deriveSessionFromIdentity(MOCK_SESSION, identity, grant),
      identity,
      session_facts,
    );
    // An employee-only runtime boots INTO the employee view regardless of
    // what the real facts say the role is. `deriveRole` resolves a genuine
    // employer (adviser / `manage_users`) to `'admin'`, and `App.tsx`
    // branches on exactly that value — so without this the native shell
    // would render the full employer console for an employer account. The
    // role is the one thing overridden; every other fact (name, tint,
    // company_name) is the real session's.
    return employee_view_only ? { ...derived, role: 'employee' } : derived;
  });
  const [update_requests, set_update_requests] =
    React.useState<UpdateRequest[]>(MOCK_UPDATE_REQUESTS);
  const [pay_runs, set_pay_runs] = React.useState<PayRun[]>(MOCK_PAY_RUNS);
  const [organisation, set_organisation] = React.useState<OrganisationSettings>(
    () =>
      deriveOrganisationSettings(
        MOCK_ORGANISATION_SETTINGS,
        organisation_record,
      ),
  );
  // The real record most recently loaded/persisted -- `update_organisation`
  // diffs the NEXT save against this (never against the mutable `organisation`
  // UI-shape state, which is already lossy for the currency-lock detour: a
  // rejected save must not have moved this forward). Only ever set from a
  // real `organisation_record` seed or a real successful save's own response
  // -- never from the mock setter path.
  const [loaded_organisation_record, set_loaded_organisation_record] =
    React.useState<RealOrganisationRecord | undefined>(organisation_record);

  const value = React.useMemo<WageProStoreValue>(
    () => ({
      session,
      is_employee_session: is_employee_session === true,
      is_employee_view_only: employee_view_only,
      set_role: (role) => {
        // Defence in depth behind hiding the affordance: an employee-only
        // runtime refuses the switch outright, so a stray call site (or a
        // devtools poke at the store) cannot render the admin surface.
        //
        // For a real EMPLOYEE session the API is the real boundary anyway —
        // their grant carries `firmId: null` and no `payroll:employer:*`
        // scope, so every admin query would 403 — but showing an admin shell
        // full of errors is its own bug. Inside the native SHELL there is no
        // such backstop: an employer's token there is fully scoped and those
        // queries would all succeed, so this check plus the forced initial
        // role (above) are the only things keeping the employer console off
        // the phone. Both halves must stay.
        if (employee_view_only && role === 'admin') return;
        set_session((current) => ({ ...current, role }));
      },

      update_requests,
      approve_update_request: (id) => {
        set_update_requests((current) =>
          current.map((request) =>
            request.id === id ? { ...request, status: 'approved' } : request,
          ),
        );
      },
      reject_update_request: (id) => {
        set_update_requests((current) =>
          current.map((request) =>
            request.id === id ? { ...request, status: 'rejected' } : request,
          ),
        );
      },

      pay_runs,
      acknowledge_exception: (run_id, exception_id) => {
        set_pay_runs((current) =>
          current.map((run) =>
            run.id === run_id
              ? {
                  ...run,
                  exceptions: run.exceptions.map((exception) =>
                    exception.id === exception_id
                      ? { ...exception, acknowledged: true }
                      : exception,
                  ),
                }
              : run,
          ),
        );
      },
      pay_run_post_actions,

      organisation,
      // The actual branching logic lives in `planOrganisationSave`
      // (`wagepro_store_settings.ts`) -- a pure, directly-unit-tested
      // function (second-round adversarial-review finding, C11 #22333: the
      // fabricated-success refusal and the empty-delta fast path were both
      // previously inlined here with zero test coverage, since no test in
      // this app renders `WageProStoreProvider`). This wrapper only performs
      // the actual I/O (`organisation_actions.save`) and applies the
      // resulting state updates.
      update_organisation: async (org) => {
        const decision = planOrganisationSave(
          organisation_actions !== undefined,
          loaded_organisation_record,
          org,
        );
        switch (decision.kind) {
          case 'refuse':
            // Defensive guard: a real controller wired up with no loaded
            // record should be structurally unreachable -- `main.tsx`'s
            // `hasRealOrganisation`/`resolveOrganisationActions` only ever
            // wire `organisation_actions` in lock-step with
            // `organisation_record` (both keyed off the same
            // `sessionFactsStatus === 'ready'` gate). Refuse rather than
            // silently falling through to the mock "always succeeds" path,
            // which would tell the caller a real save succeeded when
            // nothing was ever sent to the real API.
            console.error(
              '[wagepro-web] update_organisation: a real organisation_actions controller is wired but ' +
                'no loaded_organisation_record is present -- refusing to fabricate a mock success.',
            );
            return { ok: false, reason: 'error' };
          case 'noop':
            // Nothing actually changed from the last-persisted record -- no
            // real mutation to make. Mirrors the mock path's "always
            // succeeds" contract rather than sending an empty update.
            set_organisation(org);
            return { ok: true, record: decision.record };
          case 'send': {
            if (!organisation_actions) {
              // Unreachable: `decision.kind === 'send'` only when
              // `planOrganisationSave` was told `hasOrganisationActions`
              // (i.e. `organisation_actions !== undefined`) was true.
              // Guarded explicitly rather than a non-null assertion.
              return { ok: false, reason: 'error' };
            }
            const outcome = await organisation_actions.save(decision.delta);
            if (outcome.ok && outcome.record) {
              set_loaded_organisation_record(outcome.record);
              set_organisation(
                deriveOrganisationSettings(
                  MOCK_ORGANISATION_SETTINGS,
                  outcome.record,
                ),
              );
              return { ok: true, record: outcome.record };
            }
            // Failure (including currency_locked): `organisation` and
            // `loaded_organisation_record` are left COMPLETELY untouched --
            // the caller resets only the offending field in its own local
            // draft, never a fabricated partial success here.
            return { ok: false, reason: outcome.reason };
          }
          case 'mock':
            set_organisation(org);
            return { ok: true };
        }
      },
    }),
    [
      session,
      update_requests,
      pay_runs,
      organisation,
      loaded_organisation_record,
      pay_run_post_actions,
      organisation_actions,
      is_employee_session,
      employee_view_only,
    ],
  );

  return (
    <WageProStoreContext.Provider value={value}>
      {children}
    </WageProStoreContext.Provider>
  );
}

export function useWageProStore(): WageProStoreValue {
  const store = React.useContext(WageProStoreContext);
  if (!store) {
    throw new Error(
      'useWageProStore must be used inside <WageProStoreProvider>',
    );
  }
  return store;
}
